import { useMemo, type ReactElement } from 'react';
import { RefreshCw } from 'lucide-react';
import { useLoadToday, type FormStatus } from '@/lib/load';
import { useStravaSync } from '@/lib/strava';
import type { DashboardKpis } from '@/lib/dashboard';
import { KpiChipsRow } from './KpiChipsRow';
import { MetricTooltip } from '@/components/MetricTooltip';
import type { MetricKey } from '@/lib/metrics-glossary';

/**
 * Cabecera "hero" del dashboard principal (rediseño M2.16).
 *
 * Layout full-width:
 *  - Izquierda: saludo con nombre del profile + fecha.
 *  - Centro: 3 números grandes CTL / ATL / TSB (`text-4xl tabular-nums`) +
 *    badge de forma debajo.
 *  - Derecha: botón "Sincronizar Strava" prominente (icono `RefreshCw`) +
 *    leyenda "Hace Xh" / "Sincronizando…".
 *  - Debajo: `<KpiChipsRow kpis={kpis} />` con chips de monotonía, strain,
 *    ramp, ACWR, compliance, días sin off, próxima carrera A.
 *
 * Decisiones:
 *  - `useStravaSync` reusado (no creamos hook nuevo) — mismo botón que en
 *    `/ajustes/strava`. Si Strava no está conectado, la llamada falla 409
 *    silenciosamente (el detalle se gestiona en la card dedicada).
 *  - Si `useLoadToday` falla/no carga, mostramos sólo el saludo (degradación
 *    elegante). El resto del dashboard sigue funcionando.
 *  - `lastSyncAt` opcional: lo pasamos como prop para que el padre (Dashboard)
 *    se lo inyecte desde `data.strava.lastSyncAt`. Si no llega, mostramos
 *    "Nunca".
 */
export interface HeroStatusBarProps {
  profileName: string;
  /**
   * Bloque kpis del response del dashboard (puede ser undefined si el
   * backend aún no lo expone — es opcional en el schema).
   */
  kpis?: DashboardKpis | null;
  /** ISO 8601 del último sync de Strava (mostrar "Hace Xh"). */
  lastSyncAt?: string | null;
  /** Indica si Strava está conectado (para deshabilitar el botón si no). */
  stravaConnected?: boolean;
}

export function HeroStatusBar({
  profileName,
  kpis,
  lastSyncAt,
  stravaConnected = true,
}: HeroStatusBarProps): ReactElement {
  const loadQuery = useLoadToday();
  const syncMutation = useStravaSync();

  const today = useMemo(() => {
    const raw = new Intl.DateTimeFormat('es-ES', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(new Date());
    // Capitalizar la primera letra (Intl devuelve "sábado, 30 de mayo de 2026").
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }, []);

  const load = loadQuery.data;
  const syncPending = syncMutation.isPending;

  // El "lastSyncAt" mostrado: tras un sync exitoso preferimos el nuevo `lastSyncAt`
  // de la mutación (el dashboard tarda en invalidar y refrescar la prop).
  const effectiveLastSync = syncMutation.data?.lastSyncAt ?? lastSyncAt ?? null;

  return (
    <header
      aria-labelledby="dashboard-title"
      className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm sm:p-6"
    >
      <div className="grid gap-5 lg:grid-cols-[1fr_auto_auto] lg:items-center">
        {/* Saludo + fecha */}
        <div>
          <h1
            id="dashboard-title"
            className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl"
          >
            Hola{profileName ? `, ${profileName}` : ''}
          </h1>
          <p className="mt-1 text-sm text-slate-600">{today}</p>
        </div>

        {/* CTL / ATL / TSB grandes */}
        {load && (
          <dl
            aria-label="Estado de forma actual"
            data-testid="hero-stats"
            className="flex items-baseline gap-6"
          >
            <BigStat label="CTL" value={load.ctl.toFixed(0)} hint="Fitness (42 d)" metric="ctl" />
            <BigStat label="ATL" value={load.atl.toFixed(0)} hint="Fatiga (7 d)" metric="atl" />
            <BigStat
              label="TSB"
              value={`${load.tsb >= 0 ? '+' : ''}${load.tsb.toFixed(0)}`}
              hint="Forma (CTL−ATL)"
              metric="tsb"
            />
          </dl>
        )}

        {/* Botón sincronizar + form badge */}
        <div className="flex flex-col items-stretch gap-2 sm:items-end">
          <button
            type="button"
            onClick={() => syncMutation.mutate(undefined)}
            disabled={syncPending || !stravaConnected}
            aria-busy={syncPending}
            data-testid="hero-sync-strava"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${syncPending ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            <span>{syncPending ? 'Sincronizando…' : 'Sincronizar Strava'}</span>
          </button>
          <p
            data-testid="hero-sync-last"
            className="text-xs text-slate-500 sm:text-right"
          >
            {stravaConnected
              ? `Última: ${formatLastSync(effectiveLastSync)}`
              : 'Strava no conectado'}
          </p>
          {load && (
            <FormBadge status={load.formStatus} tsb={load.tsb} />
          )}
        </div>
      </div>

      {/* Chips KPIs derivados */}
      {kpis && (
        <div className="mt-4 border-t border-slate-100 pt-4">
          <KpiChipsRow kpis={kpis} />
        </div>
      )}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

function BigStat({
  label,
  value,
  hint,
  metric,
}: {
  label: string;
  value: string;
  hint: string;
  /** Clave del glosario M2.32.E — añade un icono "?" al lado del label. */
  metric?: MetricKey;
}): ReactElement {
  return (
    <div className="flex flex-col items-start" title={hint}>
      <dd className="text-4xl font-semibold tabular-nums leading-none text-slate-900">
        {value}
      </dd>
      <dt className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {metric ? (
          <MetricTooltip metric={metric}>{label}</MetricTooltip>
        ) : (
          label
        )}
      </dt>
    </div>
  );
}

function FormBadge({
  status,
  tsb,
}: {
  status: FormStatus;
  tsb: number;
}): ReactElement {
  const style = formStyle(status);
  return (
    <div
      data-testid="hero-form-badge"
      className={`inline-flex items-center gap-1.5 self-start rounded-full px-2.5 py-0.5 text-xs font-semibold sm:self-end ${style.className}`}
      title={`TSB ${tsb.toFixed(1)} — ${style.hint}`}
    >
      <span aria-hidden="true">{style.emoji}</span>
      <MetricTooltip metric="formStatus">{style.label}</MetricTooltip>
    </div>
  );
}

interface FormStyle {
  label: string;
  className: string;
  hint: string;
  emoji: string;
}

function formStyle(status: FormStatus): FormStyle {
  switch (status) {
    case 'fresh':
      return {
        label: 'Fresco',
        className: 'bg-emerald-100 text-emerald-900',
        hint: 'descansado, listo para pico',
        emoji: '🟢',
      };
    case 'fit':
      return {
        label: 'En forma',
        className: 'bg-teal-100 text-teal-900',
        hint: 'en forma, sigue así',
        emoji: '🟢',
      };
    case 'transition':
      return {
        label: 'Transición',
        className: 'bg-amber-100 text-amber-900',
        hint: 'fatiga acumulada, vigila',
        emoji: '🟡',
      };
    case 'overreaching':
      return {
        label: 'Sobrecarga',
        className: 'bg-rose-100 text-rose-900',
        hint: 'riesgo: necesitas recuperar',
        emoji: '🔴',
      };
  }
}

function formatLastSync(iso: string | null): string {
  if (!iso) return 'nunca';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return 'hace unos segundos';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `hace ${diffHour} h`;
  const diffDay = Math.round(diffHour / 24);
  if (diffDay < 30) return `hace ${diffDay} d`;
  return new Date(iso).toLocaleDateString('es-ES');
}
